import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { adminAPI } from '../services/api';
import { toast } from 'sonner';

const FacultyAbroadOpportunityFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(isEditing);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    duration: '',
    type: 'Research',
  });

  useEffect(() => {
    if (!isEditing) return;
    const load = async () => {
      try {
        const opportunities = await adminAPI.getFacultyAbroadOpportunities();
        const opp = opportunities.find(o => o.id === id);
        if (opp) {
          setFormData({
            title: opp.title || '',
            location: opp.location || '',
            duration: opp.duration || '',
            type: opp.type || 'Research',
          });
        } else {
          toast.error('Opportunity not found');
          navigate('/admin/dashboard');
        }
      } catch (err) {
        toast.error('Failed to load opportunity');
      } finally {
        setFetchingData(false);
      }
    };
    load();
  }, [id, isEditing, navigate]);

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.location.trim() || !formData.duration.trim()) {
      toast.error('All fields are required');
      return;
    }
    try {
      setLoading(true);
      if (isEditing) {
        await adminAPI.updateFacultyAbroadOpportunity(id, formData);
        toast.success('Opportunity updated successfully');
      } else {
        await adminAPI.createFacultyAbroadOpportunity(formData);
        toast.success('Opportunity created successfully');
      }
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.message || 'Failed to save opportunity');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-medicaps-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>
              {isEditing ? 'Edit Opportunity' : 'Add Faculty Abroad Opportunity'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g. Visiting Professor Program"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <select
                id="type"
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-medicaps-blue"
              >
                <option value="Teaching">Teaching</option>
                <option value="Research">Research</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={e => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g. USA, UK, Europe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Duration *</Label>
              <Input
                id="duration"
                value={formData.duration}
                onChange={e => setFormData({ ...formData, duration: e.target.value })}
                placeholder="e.g. 3-12 Months"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-medicaps-blue hover:bg-medicaps-blue/90"
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditing ? 'Update Opportunity' : 'Create Opportunity'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
                Cancel
              </Button>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FacultyAbroadOpportunityFormPage;